import { useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CalculatorValidationError, runCalculator } from '../calculators/engine';
import { getCalculatorBySlug } from '../calculators/registry';
import type {
  CalculatorDefinition,
  CalculatorInputValues,
  CalculatorResult,
  InputDefinition,
  OutputDefinition,
} from '../calculators/types';
import { BreakEvenDecisionPanel } from '../components/BreakEvenDecisionPanel';
import { MarginDecisionPanel } from '../components/MarginDecisionPanel';
import { RoasDecisionPanel } from '../components/RoasDecisionPanel';
import { getCalculatorSeoContent } from '../seo/calculatorContent';
import { useSeo } from '../seo/seo';
import { buildCalculatorStructuredData } from '../seo/structuredData';
import { NotFoundPage } from './NotFoundPage';

const numberFormatter = new Intl.NumberFormat('ko-KR', {
  maximumFractionDigits: 2,
});

function initialFormValues(definition: CalculatorDefinition): Record<string, string> {
  return Object.fromEntries(
    definition.inputs.map((input) => [
      input.id,
      input.type === 'select' ? input.options?.[0]?.value ?? '' : '',
    ]),
  );
}

function parseFormValues(
  definition: CalculatorDefinition,
  values: Record<string, string>,
): CalculatorInputValues {
  return Object.fromEntries(
    definition.inputs.map((input) => [
      input.id,
      input.type === 'select'
        ? values[input.id]
        : values[input.id] === ''
          ? undefined
          : Number(values[input.id].replaceAll(',', '')),
    ]),
  );
}

function groupInputs(inputs: readonly InputDefinition[]) {
  const groups: Array<{ section?: string; inputs: InputDefinition[] }> = [];

  for (const input of inputs) {
    const last = groups.at(-1);
    if (!last || last.section !== input.section) {
      groups.push({ section: input.section, inputs: [input] });
      continue;
    }
    last.inputs.push(input);
  }

  return groups;
}

function formatNumericInput(value: string): string {
  const cleaned = value.replaceAll(',', '').replace(/[^0-9.]/g, '');
  if (!cleaned) return '';

  const firstDot = cleaned.indexOf('.');
  const normalized = firstDot === -1
    ? cleaned
    : `${cleaned.slice(0, firstDot)}.${cleaned.slice(firstDot + 1).replaceAll('.', '')}`;
  const [integerPart, decimalPart] = normalized.split('.');
  const integer = integerPart.replace(/^0+(?=\d)/, '') || '0';
  const grouped = Number(integer).toLocaleString('ko-KR');
  return decimalPart === undefined ? grouped : `${grouped}.${decimalPart}`;
}

function formatResult(value: CalculatorResult[string], output: OutputDefinition): string {
  if (value === null) return '계산 불가';
  if (typeof value === 'boolean') return value ? '예' : '아니오';
  if (typeof value === 'string') return value;

  if (output.format === 'integer') return Math.round(value).toLocaleString('ko-KR');
  if (output.format === 'currency') return Math.round(value).toLocaleString('ko-KR');
  if (output.format === 'percentage') return numberFormatter.format(value);
  if (output.format === 'decimal') return numberFormatter.format(value);
  return numberFormatter.format(value);
}

export function CalculatorPage() {
  const { slug = '' } = useParams();
  const definition = getCalculatorBySlug(slug);

  if (!definition) {
    return <NotFoundPage />;
  }

  return <CalculatorScreen key={definition.slug} definition={definition} />;
}

function CalculatorScreen({ definition }: { definition: CalculatorDefinition }) {
  const structuredData = useMemo(
    () => buildCalculatorStructuredData(definition),
    [definition],
  );
  const seoContent = getCalculatorSeoContent(definition.slug);
  useSeo(definition.seo, structuredData);

  const [formValues, setFormValues] = useState<Record<string, string>>(() => initialFormValues(definition));
  const [result, setResult] = useState<CalculatorResult | null>(null);
  const [calculatedValues, setCalculatedValues] = useState<CalculatorInputValues | null>(null);
  const [error, setError] = useState<{ inputId?: string; message: string } | null>(null);
  const [copyStatus, setCopyStatus] = useState('');

  useEffect(() => {
    setCopyStatus('');
  }, [result]);

  const related = useMemo(
    () => definition.related.map(getCalculatorBySlug).filter((item): item is CalculatorDefinition => Boolean(item)),
    [definition.related],
  );
  const inputGroups = useMemo(() => groupInputs(definition.inputs), [definition.inputs]);

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setCopyStatus('');

    try {
      const parsedValues = parseFormValues(definition, formValues);
      const nextResult = runCalculator(definition, parsedValues);
      setResult(nextResult);
      setCalculatedValues(parsedValues);
    } catch (caught) {
      setResult(null);
      setCalculatedValues(null);
      if (caught instanceof CalculatorValidationError) {
        setError({ inputId: caught.inputId, message: caught.message });
        return;
      }
      setError({ message: '계산 중 오류가 발생했습니다. 입력값을 확인해 주세요.' });
    }
  }

  function reset() {
    setFormValues(initialFormValues(definition));
    setResult(null);
    setCalculatedValues(null);
    setError(null);
    setCopyStatus('');
  }

  async function copyResults() {
    if (!result) return;
    const lines = definition.outputs.map((output) => {
      const formatted = formatResult(result[output.id], output);
      return `${output.label}: ${formatted}${output.unit ? ` ${output.unit}` : ''}`;
    });
    try {
      await navigator.clipboard.writeText(`${definition.title}\n${lines.join('\n')}`);
      setCopyStatus('결과를 복사했습니다.');
    } catch {
      setCopyStatus('브라우저에서 복사를 허용하지 않았습니다.');
    }
  }

  function renderField(input: InputDefinition) {
    const errorId = `error-${input.id}`;
    const hasError = error?.inputId === input.id;

    return (
      <label className="calculator-field" key={input.id}>
        <span className="field-label">
          {input.label}
          {input.required === false ? <small>선택</small> : null}
        </span>
        {input.type === 'select' ? (
          <select
            value={formValues[input.id] ?? ''}
            onChange={(event) => setFormValues((current) => ({ ...current, [input.id]: event.target.value }))}
            aria-invalid={hasError || undefined}
            aria-describedby={hasError ? errorId : undefined}
          >
            {input.options?.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        ) : (
          <div className="input-with-unit">
            <input
              type="text"
              inputMode="decimal"
              autoComplete="off"
              value={formValues[input.id] ?? ''}
              onChange={(event) => setFormValues((current) => ({
                ...current,
                [input.id]: formatNumericInput(event.target.value),
              }))}
              aria-invalid={hasError || undefined}
              aria-describedby={hasError ? errorId : undefined}
            />
            {input.unit ? <span aria-hidden="true">{input.unit}</span> : null}
          </div>
        )}
        {hasError ? <span className="field-error" id={errorId}>{error.message}</span> : null}
      </label>
    );
  }

  return (
    <article className="calculator-page">
      <nav className="breadcrumb" aria-label="현재 위치">
        <Link to="/">홈</Link>
        <span aria-hidden="true">›</span>
        <span aria-current="page">{definition.title}</span>
      </nav>

      <header className="calculator-heading">
        <p className="eyebrow">CALCULATOR</p>
        <h1>{definition.title}</h1>
        <p>{definition.description}</p>
      </header>

      <div className="calculator-workspace">
        <form className="calculator-form" onSubmit={handleSubmit} noValidate>
          {inputGroups.map((group, index) => (
            group.section ? (
              <fieldset className="field-group" key={`${group.section}-${index}`}>
                <legend>{group.section}</legend>
                <div className="field-stack">
                  {group.inputs.map(renderField)}
                </div>
              </fieldset>
            ) : (
              <div className="field-stack" key={`default-${index}`}>
                {group.inputs.map(renderField)}
              </div>
            )
          ))}

          {error && !error.inputId ? <p className="form-error" role="alert">{error.message}</p> : null}

          <div className="calculator-actions">
            <button className="primary-button" type="submit">계산하기</button>
            <button className="secondary-button" type="button" onClick={reset}>초기화</button>
          </div>
        </form>

        <section className="calculator-results" aria-labelledby="result-title" aria-live="polite">
          <div className="result-heading-row">
            <h2 id="result-title">계산 결과</h2>
            {result ? <button className="text-button" type="button" onClick={copyResults}>결과 복사</button> : null}
          </div>
          {result ? (
            <dl className="result-list">
              {definition.outputs.map((output) => (
                <div className={output.primary ? 'result-item result-primary' : 'result-item'} key={output.id}>
                  <dt>{output.label}</dt>
                  <dd>
                    {formatResult(result[output.id], output)}
                    {result[output.id] !== null && output.unit ? <small>{output.unit}</small> : null}
                  </dd>
                </div>
              ))}
            </dl>
          ) : (
            <p className="result-placeholder">값을 입력하고 계산하면 결과가 여기에 표시됩니다.</p>
          )}
          {copyStatus ? <p className="copy-status" role="status">{copyStatus}</p> : null}
        </section>
      </div>

      {definition.slug === 'margin' && result && calculatedValues ? (
        <MarginDecisionPanel values={calculatedValues} />
      ) : null}

      {definition.slug === 'break-even' && result && calculatedValues ? (
        <BreakEvenDecisionPanel values={calculatedValues} />
      ) : null}

      {definition.slug === 'roas' && result && calculatedValues ? (
        <RoasDecisionPanel values={calculatedValues} />
      ) : null}

      {seoContent ? (
        <section className="calculator-guide" aria-labelledby="calculator-guide-title">
          <h2 id="calculator-guide-title">{definition.title} 계산 방법</h2>
          <p>{seoContent.summary}</p>
          <div className="guide-grid">
            <div>
              <h3>계산식</h3>
              <p className="guide-formula">{seoContent.formula}</p>
            </div>
            <div>
              <h3>이럴 때 사용하세요</h3>
              <ul className="guide-usage">
                {seoContent.usage.map((item) => <li key={item}>{item}</li>)}
              </ul>
            </div>
          </div>
          {seoContent.note ? <p className="guide-note">{seoContent.note}</p> : null}
        </section>
      ) : null}

      {related.length > 0 ? (
        <section className="related-calculators" aria-labelledby="related-title">
          <h2 id="related-title">관련 계산기</h2>
          <div className="related-links">
            {related.map((calculator) => (
              <Link key={calculator.slug} to={`/calculators/${calculator.slug}`}>
                {calculator.title}
              </Link>
            ))}
          </div>
        </section>
      ) : null}
    </article>
  );
}
